import React, { useMemo, useEffect } from 'react';
import { 
  FileText, CheckCircle, Clock, DollarSign, Plus
} from 'lucide-react';
import { useUsers } from '../../../metodos/userContext';
import { useSolicitudesDisponiblesModel } from '../../../modelos/Solicitudes/modeloSolicitudes';
import CardSolicitud from '../../compartedRoutes/solicitudes/CardSolicitud';
import CardServicio from '../../compartedRoutes/Contabilidad/cardServicio';
import { useContabilidadModel } from '../../../modelos/Contabilidad/modeloContabilidad';
import useClientesModel from '../../../modelos/Clientes/modeloClientes';
import { formatearPrecio } from '../../../utils/utils';
import LoadingWidget from '../../../components/Loadings/LoadingWidget';
import ErrorWidget from '../../../components/Loadings/ErrorWidget';

const SolicitudesCLiente = () => {
  const { usuario } = useUsers();
  const { solicitudesPorCliente, cargarSolicitudesDeCliente, loadingSolicitudes } = useSolicitudesDisponiblesModel();
  const { serviciosPorClientes, getServiciosPorCliente, loadingServicios } = useContabilidadModel();
  const { cliente, getCliente, loadingClientes } = useClientesModel();

  // 1. CARGA DE DATOS
  useEffect(() => {
    if (usuario?.numero) {
      cargarSolicitudesDeCliente("DUFY ASESORIAS", usuario.numero);
      getServiciosPorCliente("DUFY ASESORIAS", usuario.numero);
      getCliente("DUFY ASESORIAS", usuario.numero);
    }
  }, [usuario]);

  // 2. LÓGICA PARA OBTENER LOS ÚLTIMOS 10 (Sin filtros ni búsquedas)
  const ultimasSolicitudes = useMemo(() => {
    return [...(solicitudesPorCliente || [])]
      .sort((a, b) => new Date(b.fechasistema) - new Date(a.fechasistema))
      .slice(0, 10);
  }, [solicitudesPorCliente]);

  const ultimosServicios = useMemo(() => {
    return [...(serviciosPorClientes || [])]
      .sort((a, b) => new Date(b.fechasistema) - new Date(a.fechasistema))
      .slice(0, 10);
  }, [serviciosPorClientes]);

  // 3. MANEJO DE ESTADOS DE CARGA Y ERROR
  if (loadingServicios || loadingClientes || loadingSolicitudes) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <LoadingWidget mensaje="Cargando tu resumen de actividad..." />
      </div>
    );
  }

  if (!cliente) {
    return <ErrorWidget titulo="Error de conexión" mensaje="No pudimos sincronizar tus datos." />;
  }

  const stats = [
    { label: 'Solicitudes Realizadas', value: cliente.estadisticasCliente?.totalSolicitudes || 0, icon: <FileText className="text-blue-600" />, color: 'bg-blue-50' },
    { label: 'Servicios en Curso', value: serviciosPorClientes.filter(s => !s.getTrabajoEntregadoCliente()).length, icon: <Clock className="text-orange-600" />, color: 'bg-orange-50' },
    { label: 'Servicios Finalizados', value: cliente.estadisticasCliente?.totalServicios || 0, icon: <CheckCircle className="text-green-600" />, color: 'bg-green-50' },
    { label: 'Inversión Total', value: formatearPrecio(cliente.estadisticasCliente?.gastoTotal || 0), icon: <DollarSign className="text-purple-600" />, color: 'bg-purple-50' },
  ];

  return (
    <div className="p-4 md:p-8 space-y-10 max-w-7xl mx-auto animate-in fade-in duration-700">
      
      {/* BIENVENIDA */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-6 bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm">
        <div className="text-center md:text-left">
          <h1 className="text-2xl md:text-3xl font-black text-gray-900 leading-tight">
            Hola, {usuario.nombrecompletoCliente}
          </h1>
          <p className="text-gray-400 font-medium mt-1 text-lg">¿Cómo podemos ayudarte hoy?</p>
        </div>
        <button className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 flex items-center gap-2 active:scale-95">
          <Plus size={20} /> Nueva Solicitud
        </button>
      </div>

      {/* MÉTRICAS RÁPIDAS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex flex-col gap-3">
            <div className={`p-3 rounded-2xl w-fit ${stat.color}`}>{stat.icon}</div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{stat.label}</p>
              <p className="text-2xl font-black text-gray-800">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* SECCIONES DE ACTIVIDAD RECIENTE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        
        {/* COLUMNA: ÚLTIMAS SOLICITUDES */}
        <section className="space-y-6">
          <h2 className="text-xl font-black text-gray-800 flex items-center gap-3 px-2">
            <div className="w-2 h-8 bg-blue-500 rounded-full"></div>
            Tus últimas solicitudes
          </h2>
          <div className="space-y-4">
            {ultimasSolicitudes.length > 0 ? (
              ultimasSolicitudes.map((s) => <CardSolicitud key={s.id} solicitud={s} />)
            ) : (
              <div className="bg-gray-50 rounded-3xl p-10 text-center border-2 border-dashed border-gray-100 text-gray-400 font-medium">
                No tienes solicitudes recientes
              </div>
            )}
          </div>
        </section>

        {/* COLUMNA: ÚLTIMOS TRABAJOS */}
        <section className="space-y-6">
          <h2 className="text-xl font-black text-gray-800 flex items-center gap-3 px-2">
            <div className="w-2 h-8 bg-green-500 rounded-full"></div>
            Tus últimos trabajos realizados con nosotros
          </h2>
          <div className="space-y-4">
            {ultimosServicios.length > 0 ? (
              ultimosServicios.map((s) => <CardServicio key={s.codigo} servicio={s} view="CLIENTE" />)
            ) : (
              <div className="bg-gray-50 rounded-3xl p-10 text-center border-2 border-dashed border-gray-100 text-gray-400 font-medium">
                Aún no registras trabajos con nosotros
              </div>
            )}
          </div>
        </section>

      </div>

      {/* BOTÓN FLOTANTE */}
      <button className="fixed bottom-8 right-8 bg-blue-600 text-white p-5 rounded-full shadow-2xl hover:scale-110 transition-transform active:scale-90 z-50">
        <Plus size={30} />
      </button>

    </div>
  );
};

export default SolicitudesCLiente;